import React, { useState, useEffect, createContext, useContext } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency context for app-wide currency state
const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // CHF is the base currency (prices are set in CHF)
  const [currency, setCurrency] = useState(() => {
    // Try to get from localStorage first, default to CHF
    return localStorage.getItem('preferred_currency') || 'CHF';
  });
  // Rates FROM CHF to other currencies (CHF = 1.0)
  // Backend stores EUR=1, so we need to convert
  const [rates, setRates] = useState({ CHF: 1.0, EUR: 1.07, USD: 1.26 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch exchange rates from backend (public endpoint)
    const fetchRates = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/exchange-rates`);
        if (response.data?.rates) {
          // Rates are already CHF-based from the new public endpoint
          setRates(response.data.rates);
          console.log('Currency rates loaded:', response.data.rates);
        }
      } catch (error) {
        console.log('Using default exchange rates');
        // Default rates if API fails (CHF is base)
        setRates({ CHF: 1.0, EUR: 1.07, USD: 1.26 });
      }
    };

    // Detect currency from IP or use saved preference
    const detectCurrency = async () => {
      try {
        const savedCurrency = localStorage.getItem('preferred_currency');
        if (savedCurrency) {
          setCurrency(savedCurrency);
          setLoading(false);
          return;
        }

        // Try to detect from IP via backend
        try {
          const response = await axios.get(`${API}/subscriptions/detect-currency`);
          if (response.data?.currency) {
            setCurrency(response.data.currency);
          }
        } catch {
          // Default to CHF if detection fails (Swiss company)
          setCurrency('CHF');
        }
      } catch (error) {
        console.error('Failed to detect currency:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
    detectCurrency();
  }, []);

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
  };

  // Format price - input is always in CHF (base currency)
  const formatPrice = (priceChf, targetCurrency = currency) => {
    const rate = rates[targetCurrency] || 1.0;
    let converted = priceChf * rate;
    
    // Price smoothing only for prices >= 10
    // Small prices (< 10) are shown as-is to avoid 1 CHF becoming 1.90 CHF
    if (converted >= 10) {
      // Round to .90 (price smoothing)
      const base = Math.floor(converted);
      converted = base + 0.90;
    }
    
    // Format based on currency
    switch (targetCurrency) {
      case 'EUR':
        return `€${converted.toFixed(2)}`;
      case 'CHF':
        return `${converted.toFixed(2)} CHF`;
      case 'USD':
        return `$${converted.toFixed(2)}`;
      default:
        return `${converted.toFixed(2)} CHF`;
    }
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency: changeCurrency, rates, loading, formatPrice, baseCurrency: 'CHF' }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Currency selector dropdown component
export const CurrencySelector = ({ className = '' }) => {
  const { currency, setCurrency } = useCurrency();

  const currencies = [
    { code: 'CHF', symbol: 'CHF', name: 'Schweizer Franken' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' }
  ];

  return (
    <Select value={currency} onValueChange={setCurrency}>
      <SelectTrigger 
        className={`w-20 h-8 text-xs ${className}`}
        style={{ 
          backgroundColor: 'transparent', 
          borderColor: '#1F2937', 
          color: '#94A3B8' 
        }}
      >
        <SelectValue>
          {currencies.find(c => c.code === currency)?.symbol || currency}
        </SelectValue>
      </SelectTrigger>
      <SelectContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        {currencies.map((curr) => (
          <SelectItem 
            key={curr.code} 
            value={curr.code}
            style={{ color: '#E6EDF3' }}
          >
            <span className="flex items-center gap-2">
              <span className="font-mono">{curr.symbol}</span>
              <span className="text-xs" style={{ color: '#94A3B8' }}>{curr.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default CurrencySelector;
